// src/components/invoices/InvoiceSuccessModal.jsx
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../context/AuthContext';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    Chip,
    Divider,
    Alert,
    IconButton,
    CircularProgress,
    Link
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    GetApp as DownloadIcon,
    QrCode as QrCodeIcon,
    Print as PrintIcon,
    HourglassEmpty as HourglassEmptyIcon
} from '@mui/icons-material';
// PRODUCTION: Using real invoice service now that backend routes are deployed
import { getInvoicePDF, getInvoiceQR } from '../../services/invoiceService';
// DEVELOPMENT: import { getInvoicePDF, getInvoiceQR } from '../../services/invoiceService.mock';

function InvoiceSuccessModal({ 
    open, 
    onClose, 
    invoiceData,
    cartItems = [],
    onClearCart, 
    onKeepCart 
}) {
    const { user } = useAuth();
    const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
    const [isLoadingQR, setIsLoadingQR] = useState(false);
    const [error, setError] = useState('');

    // Format currency
    const formatPrice = useCallback((price) => {
        if (typeof price !== 'number' || isNaN(price)) {
            return 'N/A';
        }
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }, []);

    // Format date with better error handling
    const formatDate = useCallback((dateString) => {
        if (!dateString) return 'N/A';
        
        try {
            // Handle different date formats
            let date;
            
            // If it's already a Date object
            if (dateString instanceof Date) {
                date = dateString;
            }
            // If it's a string in DD/MM/YYYY format
            else if (typeof dateString === 'string' && dateString.includes('/')) {
                const [day, month, year] = dateString.split('/');
                date = new Date(year, month - 1, day);
            }
            // If it's a string in YYYY-MM-DD format
            else if (typeof dateString === 'string' && dateString.includes('-')) {
                date = new Date(dateString);
            }
            // If it's a timestamp
            else {
                date = new Date(dateString);
            }
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
                return 'N/A';
            }
            
            return date.toLocaleDateString('es-AR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', dateString, error);
            return 'N/A';
        }
    }, []);

    // Handle PDF download
    const handleDownloadPDF = useCallback(async () => {
        try {
            setIsDownloadingPDF(true);
            setError('');

            // Get invoice object safely
            const invoiceObj = invoiceData.invoice || invoiceData;
            
            // Get invoice ID from response
            const invoiceId = invoiceObj.id || invoiceObj.invoiceId;
            
            if (!invoiceId) {
                setError('No se pudo obtener el ID de la factura.');
                return;
            }
            
            // Open print page with invoice ID as URL parameter for ALL invoice types
            // The print-quote page will dynamically render based on tipo_comprobante
            const printPageUrl = `${window.location.origin}/#/print-quote?invoiceId=${invoiceId}`;
            window.open(printPageUrl, '_blank');
            
        } catch (err) {
            console.error('Error opening print page:', err);
            setError(`Error al abrir la página de impresión: ${err.message}`);
        } finally {
            setIsDownloadingPDF(false);
        }
    }, [invoiceData]);

    // Handle QR code viewing
    const handleViewQR = useCallback(async () => {
        try {
            setIsLoadingQR(true);
            setError('');

            // Get invoice object safely
            const invoiceObj = invoiceData.invoice || invoiceData;

            // Use the QR data directly from the invoice response if available
            const qrUrl = invoiceObj.afip_qr || invoiceObj.afipQr;
            
            if (qrUrl) {
                // Open the AFIP QR URL directly
                window.open(qrUrl, '_blank');
            } else {
                // Fallback to backend QR endpoint
                const qrUrlFromBackend = await getInvoiceQR(invoiceData.invoice?.id || invoiceData.invoiceId || invoiceData.id);
                window.open(qrUrlFromBackend, '_blank');
            }
        } catch (err) {
            console.error('Error loading QR code:', err);
            setError(`Error al cargar código QR: ${err.message}`);
        } finally {
            setIsLoadingQR(false);
        }
    }, [invoiceData]);

    // Handle keep cart and close
    const handleKeepCartAndClose = useCallback(() => {
        if (onKeepCart) {
            onKeepCart();
        }
        onClose();
    }, [onKeepCart, onClose]);

    // Handle clear cart and close
    const handleClearCartAndClose = useCallback(() => {
        if (onClearCart) {
            onClearCart();
        }
        onClose();
    }, [onClearCart, onClose]);

    if (!invoiceData) {
        return null;
    }

    // Handle different response structures
    // Backend returns data directly, not nested under 'invoice' property
    const invoice = invoiceData.invoice || invoiceData;
    const isQueued = !!invoiceData.queued;
    
    // Debug: Log the invoice data structure
    console.log('💳 [Success Modal] Invoice modal - invoice object:', invoice);
    console.log('💳 [Success Modal] Invoice modal - available properties:', Object.keys(invoice));
    console.log('💰 [Success Modal] Financial values check:', {
        subtotal: invoice.subtotal,
        total: invoice.total,
        discount: invoice.descuento_importe,
        discountPercent: invoice.descuento_porcentaje,
        iva: invoice.iva
    });

    return (
        <Dialog
            open={open}
            onClose={handleKeepCartAndClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '95vh', // Limit height to 95% of viewport
                    minHeight: '600px',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <DialogTitle sx={{ 
                backgroundColor: isQueued ? '#f57c00' : '#47ba8c', 
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isQueued
                        ? <HourglassEmptyIcon fontSize="large" />
                        : <CheckCircleIcon fontSize="large" />
                    }
                    <Typography variant="h5" component="div">
                        {isQueued
                            ? 'Factura en Cola de Procesamiento'
                            : (invoice.status === 'PRESUPUESTO' 
                                ? '¡Presupuesto Generado Exitosamente!' 
                                : '¡Factura Generada Exitosamente!')}
                    </Typography>
                </Box>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={handleKeepCartAndClose}
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 2, flex: 1, overflow: 'auto' }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {isQueued && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                            Factura guardada y en espera de procesamiento
                        </Typography>
                        <Typography variant="body2">
                            AFIP no está disponible en este momento. La factura fue guardada de forma segura
                            y será procesada automáticamente cuando el servicio esté disponible.
                            Revise el menú <strong>Facturas en Cola</strong> para hacer seguimiento.
                        </Typography>
                    </Alert>
                )}

                {/* Invoice Status */}}
                <Box sx={{ display: 'center', mb: 2 }}>
                    <Chip
                        icon={<CheckCircleIcon />}
                        label={`Estado: ${invoice.status === 'PRESUPUESTO' ? 'Guardado' : (invoice.afip_status || 'Aprobada')}`}
                        color="success"
                        variant="outlined"
                        size="medium"
                    />
                </Box>

                {/* Invoice Details Grid */}
                <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                    mb: 2
                }}>
                    {/* Left Column */}
                    <Box>
                        <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 1 }}>
                            {invoice.status === 'PRESUPUESTO' ? 'Datos del Presupuesto' : 'Datos de la Factura'}
                        </Typography>
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Referencia Externa
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {invoice.external_reference || invoice.externalReference}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                {invoice.status === 'PRESUPUESTO' ? 'Número de Factura X' : 'Número de Comprobante'}
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {invoice.status === 'PRESUPUESTO' 
                                    ? (invoice.presupuesto_numero || invoice.presupuestoNumero || 'Pendiente')
                                    : (invoice.numero_comprobante || invoice.numeroComprobante || 
                                       (invoice.punto_venta || invoice.puntoVenta || '0002') + '-' + 
                                       (invoice.numero || String(invoice.invoiceId || '').padStart(8, '0')))}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Fecha de Emisión
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {formatDate(invoice.fecha_emision || invoice.fechaEmision) !== 'N/A' 
                                    ? formatDate(invoice.fecha_emision || invoice.fechaEmision)
                                    : formatDate(new Date())} {/* Use today's date as fallback */}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Fecha de Vencimiento
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {formatDate(invoice.fecha_vencimiento || invoice.fechaVencimiento) !== 'N/A' 
                                    ? formatDate(invoice.fecha_vencimiento || invoice.fechaVencimiento)
                                    : formatDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))} {/* 30 days from today as default due date */}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Right Column */}
                    <Box>
                        <Typography variant="h6" gutterBottom color="primary">
                            Datos AFIP
                        </Typography>
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Número CAE
                            </Typography>
                            <Typography variant="body1" fontWeight="bold" color={invoice.cae ? "success.main" : "text.disabled"}>
                                {invoice.status === 'PRESUPUESTO' ? '-' : (invoice.cae || 'Pendiente')}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Vencimiento CAE
                            </Typography>
                            <Typography variant="body1" fontWeight="bold" color={invoice.status === 'PRESUPUESTO' ? "text.disabled" : "inherit"}>
                                {invoice.status === 'PRESUPUESTO' ? '-' : 
                                 (invoice.cae_vencimiento ? formatDate(invoice.cae_vencimiento) : 
                                  invoice.caeVencimiento ? formatDate(invoice.caeVencimiento) : 
                                  formatDate(new Date(Date.now() + 10 * 24 * 60 * 60 * 1000)))} {/* CAE typically expires in 10 days */}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Punto de Venta
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {invoice.punto_venta || invoice.puntoVenta || '0001'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Financial Summary */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                        Resumen Financiero
                    </Typography>
                    
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                        gap: 2
                    }}>
                        <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Subtotal
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {formatPrice(invoice.subtotal || invoice.total || 0)}
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Descuento ({invoice.descuento_porcentaje || 0}%)
                            </Typography>
                            <Typography variant="h6" color="warning.main">
                                -{formatPrice(invoice.descuento_importe || 0)}
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Total
                            </Typography>
                            <Typography variant="h5" color="success.main" fontWeight="bold">
                                {formatPrice(invoice.total)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    justifyContent: 'center',
                    mb: 2
                }}>
                    {!isQueued && (
                        <Button
                            variant="contained"
                            startIcon={isDownloadingPDF ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
                            onClick={handleDownloadPDF}
                            disabled={isDownloadingPDF}
                            sx={{ 
                                backgroundColor: '#4775ba',
                                '&:hover': { backgroundColor: '#3a5d94' }
                            }}
                        >
                            {isDownloadingPDF 
                                ? 'Cargando...' 
                                : (invoice.status === 'PRESUPUESTO' || invoice.invoiceType === 'X'
                                    ? 'Imprimir/Guardar Factura X'
                                    : 'Imprimir/Guardar Factura')}
                        </Button>
                    )}

                    {!isQueued && (invoice.afip_qr || invoice.afipQr) && (
                        <Button
                            variant="outlined"
                            startIcon={isLoadingQR ? <CircularProgress size={20} color="inherit" /> : <QrCodeIcon />}
                            onClick={handleViewQR}
                            disabled={isLoadingQR}
                        >
                            {isLoadingQR ? 'Cargando...' : 'Ver QR AFIP'}
                        </Button>
                    )}
                </Box>

                {/* Info about QR verification */}
                {invoice.afip_qr || invoice.afipQr && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            Puede verificar la autenticidad de esta factura escaneando el código QR AFIP 
                            o visitando: <Link href="https://servicioscf.afip.gob.ar/publico/comprobantes/cae.aspx" target="_blank">
                                Verificación AFIP
                            </Link>
                        </Typography>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, backgroundColor: '#f9f9f9' }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    width: '100%',
                    justifyContent: 'space-between'
                }}>
                    <Button
                        variant="outlined"
                        onClick={handleKeepCartAndClose}
                        sx={{ flex: 1 }}
                    >
                        Mantener Carrito
                    </Button>
                    
                    <Button
                        variant="contained"
                        onClick={handleClearCartAndClose}
                        sx={{ 
                            flex: 1,
                            backgroundColor: '#f5d540',
                            color: 'black',
                            '&:hover': { backgroundColor: '#e0c030' }
                        }}
                    >
                        Limpiar Carrito
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

InvoiceSuccessModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    invoiceData: PropTypes.shape({
        invoice: PropTypes.shape({
            id: PropTypes.number.isRequired,
            external_reference: PropTypes.string.isRequired,
            numero_comprobante: PropTypes.string,
            presupuesto_numero: PropTypes.string,  // NEW: Presupuesto number
            status: PropTypes.string,              // NEW: Status (PRESUPUESTO, etc.)
            cae: PropTypes.string,
            cae_vencimiento: PropTypes.string,
            afip_qr: PropTypes.string,
            punto_venta: PropTypes.string,
            fecha_emision: PropTypes.string.isRequired,
            fecha_vencimiento: PropTypes.string,
            subtotal: PropTypes.number.isRequired,
            descuento_porcentaje: PropTypes.number,
            descuento_importe: PropTypes.number,
            total: PropTypes.number.isRequired,
            afip_status: PropTypes.string,
        }).isRequired,
    }),
    cartItems: PropTypes.array,
    onClearCart: PropTypes.func,
    onKeepCart: PropTypes.func,
};

export default InvoiceSuccessModal;